import { CellContext, ColumnDef } from '@tanstack/react-table';
import * as Yup from 'yup';

import { getPortFacilitiesFromApi } from '../../../../forms/components/search-ports-dropdown/slices/searchPortFacilitiesSlice';
import { IspsPortType } from '../../health/slices/healthSlice';

import { formatDateTimeInTable } from '../../../../global/utils/datetimes';
import { validationOverlapDateSchema } from '../../marpol/validators';

const LEVEL_1_KEY = 'Level 1';
const LEVEL_2_KEY = 'Level 2';
const LEVEL_3_KEY = 'Level 3';

const LEVEL_1_VALUE = 'LEVEL_1';
const LEVEL_2_VALUE = 'LEVEL_2';
const LEVEL_3_VALUE = 'LEVEL_3';

interface GetPortFacilitiesProps {
  portFacilities: {
    facilityName: string;
    gisisCode: string;
    unlocode: string;
  }[];
  portUnlocode: string;
}

const formatCellValue = (props: CellContext<any, unknown>) => {
  const value = props.getValue();

  if (value === LEVEL_1_VALUE) {
    return LEVEL_1_KEY;
  } else if (value === LEVEL_2_VALUE) {
    return LEVEL_2_KEY;
  } else if (value === LEVEL_3_VALUE) {
    return LEVEL_3_KEY;
  }

  return value;
};

export const portCallsTableColumns: ColumnDef<any>[] = [
  {
    id: 'dateFrom',
    header: 'Date from',
    accessorKey: 'arrivalDate',
    cell: ({ getValue }) => formatDateTimeInTable({ value: getValue() }),
  },
  {
    id: 'dateTo',
    header: 'Date to',
    accessorKey: 'departureDate',
    cell: ({ getValue }) => formatDateTimeInTable({ value: getValue() }),
  },
  {
    id: 'port',
    header: 'Port',
    accessorKey: 'portName',
  },
  {
    id: 'country',
    header: 'Country',
    accessorKey: 'portCountry',
  },
  {
    id: 'unlocode',
    header: 'UNLOCODE',
    accessorKey: 'portUnLoCode',
  },
  {
    id: 'portFacility',
    header: 'Port Facility',
    accessorKey: 'portFacilityName',
  },
  {
    id: 'gisis',
    header: 'GISIS Code',
    accessorKey: 'portFacilityGisisCode',
  },
  {
    id: 'securityLevel',
    header: 'Security Level',
    accessorKey: 'portSecurityLevel',
    cell: formatCellValue,
  },
];

export const portSecurityLevelOptions = [
  { key: LEVEL_1_KEY, value: LEVEL_1_VALUE },
  { key: LEVEL_2_KEY, value: LEVEL_2_VALUE },
  { key: LEVEL_3_KEY, value: LEVEL_3_VALUE },
];

export const portCallsValidationSchema = (
  editingRow: number | null,
  values: IspsPortType,
  allItems: any[],
  portcallEta: string
) =>
  Yup.object().shape({
    arrivalDate: validationOverlapDateSchema(
      editingRow,
      values?.arrivalDate,
      values?.departureDate,
      allItems,
      'arrivalDate',
      'departureDate',
      portcallEta,
      true
    ),
    departureDate: validationOverlapDateSchema(
      editingRow,
      values?.arrivalDate,
      values?.departureDate,
      allItems,
      'arrivalDate',
      'departureDate',
      portcallEta
    ),
    portSecurityLevel: Yup.string().required('*Please select one'),
  });

export const portCallInitialValues = {
  arrivalDate: null,
  departureDate: null,
  portNameDropdown: null,
  portName: '',
  portUid: '',
  portcallUid: '',
  portCountry: '',
  portUnLoCode: '',
  portFacilityImoNumber: '',
  portFacilityName: '',
  newPortFacilityName: '',
  portFacilityGisisCode: '',
  portSecurityLevel: '',
};

export const getPortFacilities = async ({
  portFacilities,
  portUnlocode,
}: GetPortFacilitiesProps) => {
  const portFacilitiesFromExistingOne = portFacilities.filter(
    (portFacility) => portFacility.unlocode === portUnlocode
  );
  if (portFacilitiesFromExistingOne.length > 0) {
    return portFacilitiesFromExistingOne;
  }

  return await getPortFacilitiesFromApi({ portUnlocode: portUnlocode });
};
